import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../api/client';
import { formatStrategyLabel } from '../utils/humanLabels';

const CampaignList = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCampaigns();
  }, [clientId]);

  const fetchCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[CampaignList] Загрузка кампаний для clientId:', clientId);
      const { data } = await apiClient.get('/stats/campaigns/user');
      console.log('[CampaignList] Получены данные от API:', data);
      console.log('[CampaignList] Количество кампаний в ответе:', data?.campaigns?.length || 0);
      
      if (!data || !data.campaigns) {
        console.warn('[CampaignList] Некорректная структура ответа:', data);
        throw new Error('Некорректная структура ответа API');
      }
      
      const filtered = clientId
        ? data.campaigns.filter((c) => c.client_id === Number(clientId))
        : data.campaigns;
      
      console.log('[CampaignList] Отфильтровано кампаний:', filtered.length);
      setCampaigns(filtered);
    } catch (userErr) {
      console.warn('[CampaignList] Не удалось загрузить кампании пользователя, пробуем legacy:', userErr.message, userErr.response?.data);
      if (clientId) {
        try {
          console.log('[CampaignList] Пробуем legacy API для clientId:', clientId);
          const legacy = await apiClient.get('/stats/campaigns', {
            params: { client_id: clientId }
          });
          console.log('[CampaignList] Legacy API ответ:', legacy.data);
          if (legacy.data && legacy.data.campaigns) {
            setCampaigns(legacy.data.campaigns);
          } else {
            console.warn('[CampaignList] Legacy API вернул некорректную структуру:', legacy.data);
            setCampaigns([]);
          }
          return;
        } catch (legacyErr) {
          console.error('[CampaignList] Ошибка загрузки кампаний (legacy):', legacyErr.response?.data || legacyErr.message);
          setError('Не удалось загрузить кампании для клиента');
          setCampaigns([]);
        }
      } else {
        setError('Не удалось загрузить кампании пользователя');
        setCampaigns([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    if (typeof status === 'string') {
      return status.charAt(0).toUpperCase() + status.slice(1);
    }
    const statusMap = {
      9: 'Активна',
      11: 'Приостановлена',
      4: 'Завершена',
      7: 'Отклонена'
    };
    return statusMap[status] || `Статус ${status}`;
  };

  const getStatusColor = (status) => {
    if (typeof status === 'string') {
      return status === 'active'
        ? 'text-green-600'
        : status === 'paused' || status === 'draft'
        ? 'text-yellow-600'
        : 'text-gray-600';
    }
    const colorMap = {
      9: 'text-green-600',
      11: 'text-yellow-600',
      4: 'text-gray-600',
      7: 'text-red-600'
    };
    return colorMap[status] || 'text-gray-600';
  };

  if (loading) return <div className="glass-card p-6 text-center text-gray-600">Загрузка кампаний...</div>;
  if (error) return <div className="glass-card p-6 text-center text-red-500">{error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Рекламные кампании</h2>
        <button
          onClick={() => navigate(`/client/${clientId}`)}
          className="glass-btn-secondary px-4 py-2"
        >
          ← Назад к интерфейсу клиента
        </button>
      </div>

      <div className="grid gap-4">
        {campaigns.map(campaign => (
          <div
            key={campaign.advert_id}
            className="glass-card p-5 card-hover cursor-pointer"
            onClick={() => navigate(`/client/${clientId}/campaign/${campaign.advert_id}`)}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="text-lg font-semibold">{campaign.name}</h3>
                <p className="text-sm text-gray-600">ID: {campaign.advert_id}</p>
                <div className="flex items-center gap-4 mt-2">
                  <span className={`text-sm font-medium ${getStatusColor(campaign.status)}`}>
                    {getStatusText(campaign.status)}
                  </span>
                  <span className="text-sm text-gray-500">
                    Бюджет: {Number(campaign.budget ?? campaign.dailyBudget ?? campaign.daily_budget ?? 0).toLocaleString('ru-RU')} ₽
                  </span>
                </div>
              </div>
              
              <div className="text-right">
                <div className={`px-3 py-1.5 rounded-xl text-xs font-medium ${
                  campaign.bidder_enabled
                    ? 'bg-emerald-500/20 text-emerald-700 border border-emerald-400/30'
                    : 'bg-white/40 text-gray-600 border border-white/40'
                }`}>
                  {campaign.bidder_enabled ? 'Биддер включен' : 'Биддер выключен'}
                </div>
                {campaign.bidder_enabled && (
                  <div className="text-xs text-gray-500 mt-1">
                    {formatStrategyLabel(campaign)}
                  </div>
                )}
              </div>
            </div>
            
      <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-white/30">
        <div className="text-center">
          <div className="text-sm text-gray-500">Расходы</div>
          <div className="font-semibold">
            {(campaign.metrics?.total_spend ?? campaign.metrics?.cost ?? 0).toLocaleString()} ₽
          </div>
          <p className="text-xs text-gray-500">Бюджет: {(campaign.budget?.total_budget ?? campaign.budget ?? campaign.monthly_limit ?? 0).toLocaleString()} ₽</p>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-500">Заказы</div>
          <div className="font-semibold">
            {(campaign.metrics?.total_orders ?? campaign.metrics?.orders ?? 0).toLocaleString()}
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-500">CTR</div>
          <div className="font-semibold">
            {(campaign.metrics?.avg_ctr ?? campaign.metrics?.ctr ?? 0).toFixed(2)}%
          </div>
        </div>
        <div className="text-center">
          <div className="text-sm text-gray-500">CPC</div>
          <div className="font-semibold">
            {(campaign.metrics?.avg_cpc ?? campaign.metrics?.cpc ?? 0).toFixed(2)} ₽
          </div>
        </div>
      </div>
          </div>
        ))}
      </div>
      
      {campaigns.length === 0 && (
        <div className="glass-card text-center py-8 text-gray-500">
          У клиента нет рекламных кампаний
        </div>
      )}
    </div>
  );
};

export default CampaignList;
